import React from 'react';
import { useTranslation } from 'react-i18next';

import WalletBetrBucksIcon from '@/assets/icons/wallet-betr-bucks';
import { SocialCurrencyActiveIcon } from '@/components/SocialCurrencyActiveIcon';
import type { EntryCardPayoutProps } from '@/components/entry-card';
import { Bet } from '@/feature/bets-sbk/hooks/types';
import { useBetCost } from '@/feature/bets-sbk/hooks/use-bet-cost';
import { useFormatCurrency } from '@/hooks/use-format-currency';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';

// The active cash/coins icon (matches the legacy cards) and the purple Betr Bucks bills (bonus entry).
export const CURRENCY_ICON = <SocialCurrencyActiveIcon size={20} />;
export const CURRENCY_ICON_SM = <SocialCurrencyActiveIcon size={16} />;
export const BONUS_ICON = <WalletBetrBucksIcon size={20} />;

/** The currency icon for entry/payout amounts; Predictions renders plain dollar values without one. */
export const useSbkCurrencyIcon = () => (useIsPredictionsProduct() ? undefined : CURRENCY_ICON);
export const useSbkCurrencyIconSm = () => (useIsPredictionsProduct() ? undefined : CURRENCY_ICON_SM);

/** Entry-side of the payout block (label, stake amount, icon) shared by the open and settled SBK cards. */
export const useSbkEntryStake = (bet: Bet): Pick<EntryCardPayoutProps, 'entryLabel' | 'entryAmount' | 'entryIcon'> => {
    const { t } = useTranslation('bets');
    const { formatCurrency } = useFormatCurrency();
    const isPredictions = useIsPredictionsProduct();
    const cost = useBetCost(bet);
    const entryAmount = formatCurrency(cost);

    // Predictions positions show "Cost" with a plain dollar amount, no currency icon.
    if (isPredictions) {
        return {
            entryLabel: t('cost'),
            entryAmount,
        };
    }

    return {
        entryLabel: bet.is_betr_bucks ? t('bonus_entry') : t('entry'),
        entryAmount,
        entryIcon: bet.is_betr_bucks ? BONUS_ICON : CURRENCY_ICON,
    };
};
